import type {
  AddGroupMemberRequest,
  AssignGroupOfficerRequest,
  ChangeGroupLeaderRequest,
  DualControlResult,
  ExitGroupMemberRequest,
  GroupDetail,
  GroupHealth,
  GroupMeeting,
  GroupStatus,
  GroupSummary,
  GroupUpdateRequest,
  GroupWriteRequest,
  MeetingGenerationResult,
  MeetingLocationRequest,
  MeetingStatus,
  PagedResult,
  RecordAttendanceRequest,
  ScheduleMeetingsRequest,
} from './types'

import { apiClient } from './client'

/**
 * The group endpoints.
 *
 * Four of them are dual-controlled and answer with a {@link DualControlResult} rather than the
 * changed entity — creation, leader change, officer assignment, and the meeting location. That is
 * not an inconsistency to paper over: while a request is waiting for a countersignature there is
 * no changed entity to return, and a client that pretended otherwise would show a leader who has
 * not been appointed.
 *
 * Attendance is posted against a meeting rather than a group, which is what it belongs to — hence
 * `/meetings/{id}/attendance` and not a path under `/groups`.
 */

export interface GroupListQuery {
  /** Free text over the group code and name. */
  search?: string
  status?: GroupStatus
  branchId?: string
  loanOfficerId?: string
  page?: number
  pageSize?: number
}

export async function listGroups(query: GroupListQuery): Promise<PagedResult<GroupSummary>> {
  const response = await apiClient.get<PagedResult<GroupSummary>>('/groups', { params: query })
  return response.data
}

export async function getGroup(groupId: string): Promise<GroupDetail> {
  const response = await apiClient.get<GroupDetail>(`/groups/${encodeURIComponent(groupId)}`)
  return response.data
}

/** Dual control. Creates in `Forming`; the group code is issued when the creation executes. */
export async function createGroup(request: GroupWriteRequest): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>('/groups', request)
  return response.data
}

/**
 * Attests a group's provisional standing where it has no history to score — the countersigned
 * cold-start judgement that lets a brand-new group take its first round. Dual control: the answer
 * is an envelope, and the attestation expires rather than standing forever.
 */
export async function attestGroupHealth(groupId: string, reason: string): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>(
    `/groups/${encodeURIComponent(groupId)}/health/attestation`,
    { reason },
  )
  return response.data
}

export async function updateGroup(groupId: string, request: GroupUpdateRequest): Promise<GroupDetail> {
  const response = await apiClient.put<GroupDetail>(`/groups/${encodeURIComponent(groupId)}`, request)
  return response.data
}

export async function addGroupMember(
  groupId: string,
  request: AddGroupMemberRequest,
): Promise<GroupDetail> {
  const response = await apiClient.post<GroupDetail>(
    `/groups/${encodeURIComponent(groupId)}/members`,
    request,
  )
  return response.data
}

export async function exitGroupMember(
  groupId: string,
  customerId: string,
  request: ExitGroupMemberRequest,
): Promise<GroupDetail> {
  const response = await apiClient.post<GroupDetail>(
    `/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(customerId)}/exit`,
    request,
  )
  return response.data
}

/** Dual control. The proposed leader must already be an active member. */
export async function changeGroupLeader(
  groupId: string,
  request: ChangeGroupLeaderRequest,
): Promise<DualControlResult> {
  const response = await apiClient.put<DualControlResult>(
    `/groups/${encodeURIComponent(groupId)}/leader`,
    request,
  )
  return response.data
}

/** Dual control. The user must be flagged as a loan officer and able to act for the branch. */
export async function assignGroupOfficer(
  groupId: string,
  request: AssignGroupOfficerRequest,
): Promise<DualControlResult> {
  const response = await apiClient.put<DualControlResult>(
    `/groups/${encodeURIComponent(groupId)}/officer`,
    request,
  )
  return response.data
}

/**
 * Dual control, and on that list for a reason that is easy to miss: the geofence is what unlocks a
 * collection sheet in the field, so moving a pin or widening a radius changes where money may be
 * collected. That makes it control configuration rather than reference data.
 */
export async function setGroupMeetingLocation(
  groupId: string,
  request: MeetingLocationRequest,
): Promise<DualControlResult> {
  const response = await apiClient.put<DualControlResult>(
    `/groups/${encodeURIComponent(groupId)}/meeting-location`,
    request,
  )
  return response.data
}

/**
 * Puts meetings on the calendar and cancels what the previous schedule had put there from the new
 * start date, where those meetings have not yet happened. Meetings already held, missed, or in
 * progress are untouched — they are records of what happened, not plans.
 */
export async function scheduleGroupMeetings(
  groupId: string,
  request: ScheduleMeetingsRequest,
): Promise<MeetingGenerationResult> {
  const response = await apiClient.post<MeetingGenerationResult>(
    `/groups/${encodeURIComponent(groupId)}/meetings/schedule`,
    request,
  )
  return response.data
}

export interface GroupMeetingQuery {
  from?: string
  to?: string
  status?: MeetingStatus
  page?: number
  pageSize?: number
}

export async function listGroupMeetings(
  groupId: string,
  query: GroupMeetingQuery,
): Promise<PagedResult<GroupMeeting>> {
  const response = await apiClient.get<PagedResult<GroupMeeting>>(
    `/groups/${encodeURIComponent(groupId)}/meetings`,
    { params: query },
  )
  return response.data
}

export async function recordMeetingAttendance(
  meetingId: string,
  request: RecordAttendanceRequest,
): Promise<GroupMeeting> {
  const response = await apiClient.post<GroupMeeting>(
    `/meetings/${encodeURIComponent(meetingId)}/attendance`,
    request,
  )
  return response.data
}

/** The latest stored snapshot. This does not compute anything. */
export async function getGroupHealth(groupId: string): Promise<GroupHealth> {
  const response = await apiClient.get<GroupHealth>(`/groups/${encodeURIComponent(groupId)}/health`)
  return response.data
}

/**
 * Computes now and writes a snapshot.
 *
 * Gated on `groups.health.view` rather than a permission of its own. Recomputation is
 * deterministic over stored records — it cannot produce a better score than the data supports,
 * only a fresher one — and a stale score is itself a risk at round application.
 */
export async function recomputeGroupHealth(groupId: string): Promise<GroupHealth> {
  const response = await apiClient.post<GroupHealth>(
    `/groups/${encodeURIComponent(groupId)}/health/recompute`,
  )
  return response.data
}
